import React, { useState } from 'react';
import { useFinanceStore } from '../api/useFinanceStore';
import { ArrowDownLeft, ArrowUpRight, Plus, X, CreditCard, Banknote, Wallet, Trash2, Edit2, Check, Ban } from 'lucide-react';
import { formatZAR } from '../../../utils/formatters';

const paymentMethodIcon = (method: string) => {
    switch (method?.toLowerCase()) {
        case 'bank_transfer': case 'bank transfer': case 'eft': return <Banknote size={18} />;
        case 'card': case 'credit_card': case 'credit card': return <CreditCard size={18} />;
        default: return <Wallet size={18} />;
    }
};

const PaymentsView: React.FC = () => {
    const { payments, invoices, logPayment, updatePayment, deletePayment } = useFinanceStore();
    const [showLogPayment, setShowLogPayment] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [payInvoiceId, setPayInvoiceId] = useState('');
    const [payAmount, setPayAmount] = useState('');
    const [payMethod, setPayMethod] = useState('bank_transfer');
    const [payReference, setPayReference] = useState('');
    const [editAmount, setEditAmount] = useState('');
    const [editMethod, setEditMethod] = useState('');
    const [editReference, setEditReference] = useState('');

    const unpaidInvoices = invoices.filter(i => i.status === 'unpaid' || i.status === 'partial' || i.status === 'overdue');

    const resolveInvoiceNumber = (invoiceId?: string) => {
        if (!invoiceId) { return null; }
        const inv = invoices.find(i => i.id === invoiceId);
        return inv ? (inv.invoiceNumber || inv.id) : invoiceId;
    };

    const resolveClientName = (invoiceId?: string) => {
        if (!invoiceId) { return null; }
        const inv = invoices.find(i => i.id === invoiceId);
        return inv?.clientName || null;
    };

    const handleLogPayment = async () => {
        if (!payAmount) { return; }
        await logPayment({
            invoiceId: payInvoiceId || undefined,
            amount: parseFloat(payAmount),
            method: payMethod,
            reference: payReference,
            direction: 'inflow',
        });
        setShowLogPayment(false);
        setPayInvoiceId('');
        setPayAmount('');
        setPayMethod('bank_transfer');
        setPayReference('');
    };

    const handleStartEdit = (payment: typeof payments[0]) => {
        setEditingId(payment.id);
        setEditAmount(String(payment.amount));
        setEditMethod(payment.method);
        setEditReference(payment.reference);
    };

    const handleSaveEdit = async (id: string) => {
        await updatePayment(id, {
            amount: parseFloat(editAmount),
            method: editMethod,
            reference: editReference,
        });
        setEditingId(null);
    };

    const totalInflow = payments.filter(p => p.direction === 'inflow').reduce((s, p) => s + p.amount, 0);
    const totalOutflow = payments.filter(p => p.direction === 'outflow').reduce((s, p) => s + p.amount, 0);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">Payments</h3>
                <div className="flex gap-2">
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                        In: {formatZAR(totalInflow)}
                    </span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                        Out: {formatZAR(totalOutflow)}
                    </span>
                    <button
                        onClick={() => setShowLogPayment(true)}
                        className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all active:scale-95"
                    >
                        <Plus size={12} /> Log Payment
                    </button>
                </div>
            </div>

            {/* Log Payment Form */}
            {showLogPayment && (
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-black text-blue-800 uppercase tracking-widest">Record Payment</div>
                        <button onClick={() => setShowLogPayment(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <select
                            value={payInvoiceId}
                            onChange={e => {
                                setPayInvoiceId(e.target.value);
                                const inv = invoices.find(i => i.id === e.target.value);
                                if (inv) { setPayAmount(String(inv.amount)); }
                            }}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400 bg-white"
                        >
                            <option value="">Select Invoice (optional)</option>
                            {unpaidInvoices.map(inv => (
                                <option key={inv.id} value={inv.id}>{inv.invoiceNumber || inv.id} — {inv.clientName || inv.clientId} — {formatZAR(inv.amount)}</option>
                            ))}
                        </select>
                        <input
                            type="number"
                            placeholder="Amount (ZAR)"
                            value={payAmount}
                            onChange={e => setPayAmount(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400"
                        />
                        <select
                            value={payMethod}
                            onChange={e => setPayMethod(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400 bg-white"
                        >
                            <option value="bank_transfer">Bank Transfer (EFT)</option>
                            <option value="card">Card Payment</option>
                            <option value="cash">Cash</option>
                            <option value="other">Other</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Reference"
                            value={payReference}
                            onChange={e => setPayReference(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400"
                        />
                    </div>
                    <button
                        onClick={handleLogPayment}
                        disabled={!payAmount}
                        className="w-full py-2 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Record Payment
                    </button>
                </div>
            )}

            {/* Payment History */}
            <div className="space-y-3">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Payment History</div>
                {payments.length === 0 ? (
                    <div className="p-10 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-300 text-slate-400 font-medium">
                        No payments recorded yet. Log your first payment above.
                    </div>
                ) : (
                    <div className="grid gap-3">
                        {payments.map(payment => (
                            <div key={payment.id} className="p-4 bg-white rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between group hover:border-blue-300 transition-all">
                                {editingId === payment.id ? (
                                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                                        <input type="number" value={editAmount} onChange={e => setEditAmount(e.target.value)} className="px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                        <select value={editMethod} onChange={e => setEditMethod(e.target.value)} className="px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white">
                                            <option value="bank_transfer">Bank Transfer</option>
                                            <option value="card">Card</option>
                                            <option value="cash">Cash</option>
                                            <option value="other">Other</option>
                                        </select>
                                        <input type="text" value={editReference} onChange={e => setEditReference(e.target.value)} placeholder="Reference" className="px-2 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
                                        <div className="flex gap-1">
                                            <button onClick={() => handleSaveEdit(payment.id)} className="p-1 text-green-600 hover:bg-green-50 rounded-lg" title="Save"><Check size={16} /></button>
                                            <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-50 rounded-lg" title="Cancel"><Ban size={16} /></button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 rounded-xl ${payment.direction === 'inflow' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                                {payment.direction === 'inflow' ? <ArrowDownLeft size={20} /> : <ArrowUpRight size={20} />}
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-800">
                                                    {formatZAR(payment.amount)}
                                                    <span className="text-slate-400 font-normal ml-2">• {payment.method}</span>
                                                </div>
                                                <div className="text-xs text-slate-600">
                                                    {resolveInvoiceNumber(payment.invoiceId) && (
                                                        <span className="font-semibold">Inv: {resolveInvoiceNumber(payment.invoiceId)}</span>
                                                    )}
                                                    {resolveClientName(payment.invoiceId) && (
                                                        <span className="text-slate-400"> • {resolveClientName(payment.invoiceId)}</span>
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
                                                    {payment.date} {payment.reference && `• Ref: ${payment.reference}`}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {paymentMethodIcon(payment.method)}
                                            <button
                                                onClick={() => handleStartEdit(payment)}
                                                className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                                                title="Edit"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                            <button
                                                onClick={() => deletePayment(payment.id)}
                                                className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                                                payment.direction === 'inflow' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                                            }`}>
                                                {payment.direction}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentsView;
